/** @format */

import {
	CRITEO_DIGITAL_SHELF_TALKER_PINNED_POSITION,
	CRITEO_PINNED_CONTENT_REQUIRED_ATTRIBUTES,
	EMPTY_CRITEO_VIEW_ITEM_RESPONSE,
	EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE
} from './criteo.constants';
import {
	CertonaSchemeItem,
	Chain,
	CriteoCarouselPlacement,
	CriteoDigitalShelfTalker,
	CriteoDigitalShelfTalkerRendering,
	CriteoInGridPlacement,
	CriteoPinnedContentSpot,
	CriteoPlacement,
	CriteoProduct,
	CriteoTrackTransactionResponse,
	CriteoTransactionMetadata,
	CriteoViewItemMetadata,
	CriteoViewItemResponse,
	CriteoViewProductsMetadata,
	CriteoViewProductsResponse,
	Environment,
	PlpInGridSponsoredProduct,
	PriceObject,
	ProductSku,
	ProductTypes
} from '@shared/data-access/interfaces';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, timeout } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Logger } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class CriteoService {
	private readonly CRITEO_API_URL = '';
	lastInGridOnLoadBeacon = ''; //This is used for the inGrid onLoadBeacons. This is to prevent two criteo calls to be made
	lastDSTOnLoadBeacon = '';

	// PLP/SRLP Global variables
	everyThirdSlot = 0;
	pinnedContentInFirstRow = 0;
	sponsoredShelfPositions: number[] = [4, 5, 6, 7];
	shelfChecked = false;
	// Performance optimizations: Cache frequently used values
	private authStateCacheTime = 0;
	private cachedAuthState: string | null = null;
	private readonly AUTH_CACHE_DURATION = 30000; // 30 seconds
	private readonly cachedDefaultParams: Map<string, HttpParams> = new Map();

	// Pre-compiled regex patterns for better performance
	private readonly COLOR_QUOTE_REGEX = /"([^"]+)"/g;
	private readonly COLOR_REGEX = /Color:\[(.*?)\]/;
	private readonly PRICE_REGEX = /Price:\[(.*?)\]/;

	// Pre-defined object templates to reduce object creation
	private readonly EMPTY_ARRAY = [];
	private readonly defaultPLPProductTemplate = {
		Color: [''],
		CustomerRating: '',
		ID: '',
		ImageURL: '',
		Name: '',
		mapPriceIndicator: 0,
		maxlistprice: '',
		maxmapprice: '',
		maxofferprice: '',
		minlistprice: '',
		minmapprice: '',
		minofferprice: '',
		offerprice: '',
		onlineInventoryMessage: ''
	};

	private readonly defaultCertonaTemplate = {
		click_tracker: '',
		impression_tracker: '',
		BreakingMap: '',
		CustomerRating: '',
		ID: '',
		ImageURL: '',
		Name: '',
		ReviewCount: '',
		SKUID: '',
		URL: '',
		WCSIdentifier: '',
		isSkuRecommendation: false,
		mapPriceIndicator: 0,
		maxlistprice: '',
		maxmapprice: '',
		maxofferprice: '',
		minlistprice: '',
		minmapprice: '',
		minofferprice: '',
		offerprice: ''
	};

	constructor(
		private readonly chain: Chain,
		private readonly environment: Environment,
		private readonly http: HttpClient,
		private readonly logger: Logger
	) {
		this.CRITEO_API_URL = this.environment.api.criteo.baseUrl;
	}

	public parseFilters(filters: Record<string, string[]>): string {
		const filterEntries = Object.entries(filters);

		if (filterEntries.length === 0) {
			return '';
		}

		const filterStrings = filterEntries.map(
			([key, values]) => `(${key},${this.formatFilterValues(values)})`
		);

		return filterStrings.join(',');
	}

	private formatFilterValues(values: string[]): string {
		const n = values.length;

		// Check if values array is empty
		if (n === 0) {
			return '';
		}
		// Pre-allocate array size for better performance
		const formattedValues = new Array(n);

		for (let i = 0; i < n; i++) {
			const value = values[i];
			if (value.startsWith('*-')) {
				formattedValues[i] = `Under ${value.slice(2)}`;
			} else if (value.endsWith('-*')) {
				formattedValues[i] = `Over ${value.slice(0, -2)}`;
			} else {
				formattedValues[i] = value;
			}
		}

		return formattedValues.length === 1
			? `eq,${formattedValues[0]}`
			: `in,${formattedValues.join(',')}`;
	}

	public transformToCriteoBreadcrumbFormat(input: string): string {
		const parts = input.split('_');
		return parts.slice(1).join('>');
	}

	private getCachedAuthState(): string | null {
		const now = Date.now();
		if (this.cachedAuthState && now - this.authStateCacheTime <= this.AUTH_CACHE_DURATION) {
			return this.cachedAuthState;
		}

		// Read synchronously from localStorage
		const newAuthState = window.localStorage.getItem('TAG_AuthState');

		// If auth state changed, clear the params cache to prevent stale data
		if (this.cachedAuthState !== newAuthState) {
			this.cachedDefaultParams.clear();
		}

		this.cachedAuthState = newAuthState;
		this.authStateCacheTime = Date.now();
		return this.cachedAuthState;
	}

	private getDefaultParams(eventType: string): HttpParams {
		const isLoggedIn = this.getCachedAuthState() === 'true';

		// Create cache key that includes auth state to prevent stale data
		const cacheKey = `${eventType}_${isLoggedIn ? 'authenticated' : 'anonymous'}`;
		const cachedParams = this.cachedDefaultParams.get(cacheKey);
		if (cachedParams) {
			return cachedParams;
		}

		let params = new HttpParams()
			.set(
				'criteo-partner-id',
				this.environment.api.criteo.partnerId[this.chain.chainIdentifierAbbr]
			)
			.set('retailer-visitor-id', window._dsgTag?.CustomerIDs?.AdobeMCVID)
			.set('environment', window._dsgTag?.experience === 'Mobile' ? 'm' : 'd')
			.set('nolog', this.environment.api.criteo.nolog)
			.set('event-type', eventType)
			.set('page-id', `${eventType}_API_${window._dsgTag?.experience?.toLowerCase()}`);

		if (isLoggedIn) {
			params = params
				.set('email', window._dsgTag?.CustomerIDs?.HashedEmail)
				.set('customer-id', window._dsgTag?.CustomerIDs?.AthleteID);
		}

		this.cachedDefaultParams.set(cacheKey, params);
		return params;
	}

	private isOptedOut(): boolean {
		if (window._dsgTag?.DoNotSell) {
			console.warn('Athlete opted out, not sending request to Criteo.');
			return true;
		}
		return false;
	}

	private handleError(error: any): Observable<CriteoViewProductsResponse> {
		if (error?.name === 'TimeoutError') {
			this.logger.error('Criteo API request timed out');
		} else {
			this.logger.error('Criteo call failed: ', error);
		}
		return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
	}

	public trackTransaction(
		transactionMetadata: CriteoTransactionMetadata
	): Observable<CriteoTrackTransactionResponse> {
		if (this.isOptedOut()) {
			return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
		}

		const defaultParams = this.getDefaultParams('trackTransaction');
		let httpParams = defaultParams;
		httpParams = this.addParamIfPresent(httpParams, 'item', transactionMetadata.items); // '123|456|789'
		httpParams = this.addParamIfPresent(httpParams, 'price', transactionMetadata.prices); // '2.99|23.99|54.49'
		httpParams = this.addParamIfPresent(httpParams, 'quantity', transactionMetadata.quantities); // '1|4|1'
		httpParams = this.addParamIfPresent(
			httpParams,
			'transaction-id',
			transactionMetadata.transactionId
		);
		httpParams = this.addParamIfPresent(httpParams, 'currency', transactionMetadata.currency);

		return this.http
			.get<CriteoTrackTransactionResponse>(this.CRITEO_API_URL, {
				params: httpParams
			})
			.pipe(catchError(this.handleError.bind(this)));
	}

	public viewItem(viewItemMetadata: CriteoViewItemMetadata): Observable<CriteoViewItemResponse> {
		if (this.isOptedOut()) {
			return of({ ...EMPTY_CRITEO_VIEW_ITEM_RESPONSE });
		}

		const defaultParams = this.getDefaultParams('viewItem');
		let httpParams = defaultParams;
		httpParams = this.addParamIfPresent(httpParams, 'item', viewItemMetadata.item);
		httpParams = this.addParamIfPresent(httpParams, 'parent-item', viewItemMetadata.parentItem);
		httpParams = this.addParamIfPresent(httpParams, 'category', viewItemMetadata.category);
		httpParams = this.addParamIfPresent(httpParams, 'price', viewItemMetadata.price);
		httpParams = this.addParamIfPresent(
			httpParams,
			'availability',
			viewItemMetadata.availability
		);

		return this.http
			.get<CriteoViewItemResponse>(this.CRITEO_API_URL, { params: httpParams })
			.pipe(
				timeout(1000),
				map((response: CriteoViewItemResponse) => {
					const placements = response.placements.map((placement) => {
						const carousel = Object.values(placement).flat();
						return { carousel };
					});

					if (placements.length === 0) {
						placements.push({ carousel: [{ products: [] } as CriteoPlacement] });
					}

					return {
						OnLoadBeacon: response.OnLoadBeacon,
						placements
					};
				}),
				catchError(this.handleError.bind(this))
			);
	}

	/**
	 * Adds a parameter to the HttpParams object if the value is not null, undefined, or an empty string.
	 *
	 * @param params The original HttpParams object to which the parameter will be added.
	 * @param key The key of the parameter to be added.
	 * @param value The value of the parameter. It can be a string, number, or boolean. Expected format: '123|456|789'
	 * If the value is null, undefined, or an empty string, the parameter will not be added.
	 * @return A new HttpParams object with the parameter added, or the original object if the value was not present.
	 */
	private addParamIfPresent(
		params: HttpParams,
		key: string,
		value: string | number | boolean | undefined
	): HttpParams {
		return value !== null && value !== undefined && value !== ''
			? params.set(key, value.toString())
			: params;
	}

	public viewProductsPage(
		viewProductsMetadata: CriteoViewProductsMetadata
	): Observable<CriteoViewProductsResponse> {
		if (this.isOptedOut()) {
			return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
		}

		const eventType =
			ProductTypes.VIEW_CATEGORY === viewProductsMetadata.productType
				? 'viewCategory'
				: 'viewSearchResult';
		const defaultParams = this.getDefaultParams(eventType);

		let httpParams = defaultParams;
		httpParams = this.addParamIfPresent(httpParams, 'list-size', viewProductsMetadata.listSize);
		httpParams = this.addParamIfPresent(
			httpParams,
			'page-number',
			viewProductsMetadata.pageNumber + 1 // Criteo API is 1-indexed
		);
		httpParams = this.addParamIfPresent(httpParams, 'filters', viewProductsMetadata.filters);
		httpParams = this.addParamIfPresent(httpParams, 'item', viewProductsMetadata.item);
		httpParams = this.addParamIfPresent(
			httpParams,
			'parent-item',
			viewProductsMetadata.parentItem
		);

		if (viewProductsMetadata.productType === ProductTypes.VIEW_CATEGORY) {
			httpParams = this.addParamIfPresent(
				httpParams,
				'category',
				viewProductsMetadata.category
			);
		} else {
			httpParams = this.addParamIfPresent(
				httpParams,
				'keywords',
				viewProductsMetadata.category
			);
		}

		return this.http
			.get<CriteoViewProductsResponse>(this.CRITEO_API_URL, { params: httpParams })
			.pipe(
				map((response: CriteoViewProductsResponse) => {
					const carousel = [];
					const inGrid = [];
					let digitalShelfTalker: CriteoDigitalShelfTalker;

					for (const placement of response.placements) {
						const [key, value] = Object.entries(placement)[0];
						if (key.toLowerCase().includes('carousel')) {
							carousel.push(value[0]);
						} else if (key.toLowerCase().includes('ingrid')) {
							inGrid.push(value[0]);
						} else if (key.toLowerCase().includes('digital shelf talker')) {
							digitalShelfTalker = value[0];
						}
					}

					if (carousel.length === 0) {
						carousel.push({ products: [] } as CriteoPlacement);
					}
					if (inGrid.length === 0) {
						inGrid.push({ products: [] } as CriteoPlacement);
					}

					const carouselPlacement: CriteoCarouselPlacement = { carousel };
					const inGridPlacement: CriteoInGridPlacement = { inGrid };

					const res: CriteoViewProductsResponse = {
						OnLoadBeacon: response.OnLoadBeacon,
						placements: [carouselPlacement, inGridPlacement] as [
							CriteoCarouselPlacement,
							CriteoInGridPlacement
						]
					};
					if (digitalShelfTalker) {
						res.placements.push({ digitalShelfTalker });
					}

					return res;
				}),
				catchError(this.handleError.bind(this))
			);
	}

	/*
		@param
			beaconUrl - Expect unmodified url from criteo
			excluded - optional, expects productIds separated by pipe
				Ex. 123|456|098
	*/
	public fireBeacon(beaconUrl: string, excluded?: string): void {
		if (!beaconUrl) return;

		const finalUrl = excluded ? `${beaconUrl}&ex=${excluded}` : beaconUrl;

		if (navigator.sendBeacon) {
			navigator.sendBeacon(finalUrl);
			return;
		}

		// If sendBeacon is not available, use Image to fire beacon.
		const img = new Image();
		img.src = finalUrl;
	}

	public transformCriteoProducts(
		sponsoredProducts: CriteoProduct[],
		forCertona = false
	): CertonaSchemeItem[] | PlpInGridSponsoredProduct[] {
		if (!sponsoredProducts.length) {
			return this.EMPTY_ARRAY;
		}

		const criteoDisplayables: (CertonaSchemeItem | PlpInGridSponsoredProduct)[] = [];

		for (const product of sponsoredProducts) {
			const formattedProduct = forCertona
				? this.getFormattedProductForCarousel(product)
				: this.getFormattedProductForPLP(product);

			this.processProductRenderingAttributes(product, formattedProduct, forCertona);
			criteoDisplayables.push(formattedProduct);
		}

		return forCertona
			? (criteoDisplayables as CertonaSchemeItem[])
			: (criteoDisplayables as PlpInGridSponsoredProduct[]);
	}

	private processProductRenderingAttributes(
		product: CriteoProduct,
		formattedProduct: CertonaSchemeItem | PlpInGridSponsoredProduct,
		forCertona: boolean
	): void {
		try {
			const renderingAttr = JSON.parse(product.RenderingAttributes);
			const renderingAttributesString = renderingAttr.rendering_attributes;
			const priceMatch = renderingAttributesString.match(this.PRICE_REGEX);

			if (priceMatch) {
				this.processPriceData(priceMatch[1], formattedProduct, forCertona);

				if (!forCertona) {
					this.extractAndSetColors(
						renderingAttributesString,
						formattedProduct as PlpInGridSponsoredProduct
					);
				}
			}
		} catch (error) {
			this.logger.error('Error parsing rendering attributes: ', {
				error,
				renderingAttr: product.RenderingAttributes
			});
		}
	}

	private processPriceData(
		priceMatchString: string,
		formattedProduct: CertonaSchemeItem | PlpInGridSponsoredProduct,
		forCertona: boolean
	): void {
		const jsonString = `{${priceMatchString.replace(/'/g, '"')}}`;
		const priceObject = JSON.parse(jsonString);

		if (forCertona) {
			this.updateCertonaProductPrices(formattedProduct as CertonaSchemeItem, priceObject);
		} else {
			this.updatePLPProductPrices(formattedProduct as PlpInGridSponsoredProduct, priceObject);
		}
	}

	private extractAndSetColors(
		renderingAttributesString: string,
		formattedProduct: PlpInGridSponsoredProduct
	): void {
		const colorMatch = this.COLOR_REGEX.exec(renderingAttributesString);
		if (colorMatch) {
			const colors: string[] = [];
			let match: RegExpExecArray | null;
			while ((match = this.COLOR_QUOTE_REGEX.exec(colorMatch[1])) !== null) {
				colors.push(match[1]);
			}
			this.COLOR_QUOTE_REGEX.lastIndex = 0; // Reset regex state for future calls
			formattedProduct.product.Color = colors;
		}
	}

	private updateCertonaProductPrices(product: CertonaSchemeItem, priceObject: PriceObject): void {
		product.mapPriceIndicator = priceObject?.mapPriceIndicator ?? product.mapPriceIndicator;
		product.maxlistprice = priceObject?.maxlistprice ?? product.maxlistprice;
		product.maxmapprice = priceObject?.maxmapprice ?? product.maxmapprice;
		product.maxofferprice = priceObject?.maxofferprice ?? product.maxofferprice;
		product.minlistprice = priceObject?.minlistprice ?? product.minlistprice;
		product.minmapprice = priceObject?.minmapprice ?? product.minmapprice;
		product.minofferprice = priceObject?.minofferprice ?? product.minofferprice;
	}

	private updatePLPProductPrices(
		product: PlpInGridSponsoredProduct,
		priceObject: PriceObject
	): void {
		product.product.mapPriceIndicator =
			priceObject?.mapPriceIndicator ?? product.product.mapPriceIndicator;
		product.product.maxlistprice = priceObject?.maxlistprice ?? product.product.maxlistprice;
		product.product.maxmapprice = priceObject?.maxmapprice ?? product.product.maxmapprice;
		product.product.maxofferprice = priceObject?.maxofferprice ?? product.product.maxofferprice;
		product.product.minlistprice = priceObject?.minlistprice ?? product.product.minlistprice;
		product.product.minmapprice = priceObject?.minmapprice ?? product.product.minmapprice;
		product.product.minofferprice = priceObject?.minofferprice ?? product.product.minofferprice;
	}

	private getFormattedProductForPLP(product: CriteoProduct): PlpInGridSponsoredProduct {
		return {
			click_tracker: product.OnClickBeacon,
			impression_tracker: product.OnViewBeacon,
			onBasketChangeBeacon: product.OnBasketChangeBeacon,
			onWishlistBeacon: product.OnWishlistBeacon,
			isCriteo: true,
			sponsored: true,
			product_id: product.ParentSKU,
			product: {
				...this.defaultPLPProductTemplate,
				CustomerRating: product.Rating,
				ID: product.ProductId,
				ImageURL: product.Image,
				Name: (() => {
					try {
						return decodeURIComponent(product.ProductName);
					} catch (error) {
						return product.ProductName;
					}
				})()
			}
		};
	}

	private getFormattedProductForCarousel(product: CriteoProduct): CertonaSchemeItem {
		return {
			...this.defaultCertonaTemplate,
			click_tracker: product.OnClickBeacon,
			impression_tracker: product.OnViewBeacon,
			CustomerRating: product.Rating,
			ID: product.ProductId,
			ImageURL: product.Image,
			Name: (() => {
				try {
					return decodeURIComponent(product.ProductName);
				} catch (error) {
					this.logger.error('Failed to decode URI: ', error);
					return product.ProductName;
				}
			})(),
			SKUID: product.ParentSKU,
			URL: product.ProductPage
		};
	}

	//PLP Functions:
	public fireInGridOnLoadBeacon(beaconURL: string, excludedProducts = ''): void {
		if (!beaconURL || beaconURL === this.lastInGridOnLoadBeacon) {
			return;
		}
		this.fireBeacon(beaconURL, excludedProducts);
		this.lastInGridOnLoadBeacon = beaconURL;
	}

	public fireDSTOnLoadBeacon(beaconURL: string): void {
		if (!beaconURL || beaconURL === this.lastDSTOnLoadBeacon) {
			return;
		}
		this.fireBeacon(beaconURL);
		this.lastDSTOnLoadBeacon = beaconURL;
	}

	public isSponsoredProduct(
		sponsoredProducts: PlpInGridSponsoredProduct[] | null,
		productCardCount: number,
		pageNumber: number,
		filters: Record<string, string[]>,
		featureFlags: Record<string, boolean>,
		pinnedContent
	): boolean {
		// Ensure there are sponsored products available to place
		// Also ensure that if the product isn't a criteo product and we are either filtering or not on the first page, return false.
		if (
			!sponsoredProducts?.length ||
			(!sponsoredProducts[0]?.isCriteo &&
				(pageNumber !== 0 || Object.keys(filters).length !== 0))
		) {
			return false;
		}

		// Only run this if we are using the sponsored shelf and if we haven't checked yet
		if (featureFlags.isSPAShelfEnabled && !this.shelfChecked) {
			for (let i = 0; i < 4; i++) {
				if (typeof pinnedContent?.[i] !== 'undefined') {
					this.pinnedContentInFirstRow++;
				}
			}
			this.sponsoredShelfPositions = this.sponsoredShelfPositions.map(
				(pos) => pos - this.pinnedContentInFirstRow
			);
			this.everyThirdSlot = this.sponsoredShelfPositions[3] % 3;
			this.shelfChecked = true;
		}

		// If the shelf is enabled and the pCC is equal to one of the shelf position numbers, return true.
		// If the second position is enabled and we are at the second position (pCC is equal to 1), return true.
		// If we are past the 7th position (pCC >= 6), check for every third position (calculated above). If we are at a third position, return true.
		// Otherwise, return false.
		return (
			(this.sponsoredShelfPositions.includes(productCardCount) &&
				featureFlags.isSPAShelfEnabled) ||
			(productCardCount === 1 && featureFlags.isSPAPosition2Enabled) ||
			(productCardCount >= 6 && productCardCount % 3 === this.everyThirdSlot)
		);
	}

	/**
	 * Creates CriteoViewItemMetadata from a ProductSku for pdp templates
	 * @param productSku The first SKU from the product
	 * @returns CriteoViewItemMetadata object
	 */
	public createViewItemMetadata(productSku: ProductSku): CriteoViewItemMetadata {
		return {
			item: productSku?.partNumber,
			price: productSku?.prices?.offerPrice?.toString(),
			availability: productSku?.buyable,
			parentItem: productSku?.parentPartNumber
		};
	}

	public checkOnLoadBeaconFiring(
		sponsoredSEO: string,
		beaconURL: string,
		sponProductCopy: PlpInGridSponsoredProduct[] | null,
		skippedIDs: string[]
	): void {
		if (sponsoredSEO === 'N') {
			return;
		}

		if (sponProductCopy?.length) {
			for (const skipped of sponProductCopy) {
				skippedIDs.push(skipped.product.ID);
			}
		}

		const excludedProductsString = skippedIDs.join('|');
		this.fireInGridOnLoadBeacon(beaconURL, excludedProductsString);
	}

	public transformToPinnedContent(dst: CriteoDigitalShelfTalker): CriteoPinnedContentSpot | null {
		const dstr: CriteoDigitalShelfTalkerRendering = dst.rendering;
		const missingAttributes = CRITEO_PINNED_CONTENT_REQUIRED_ATTRIBUTES.find(
			(attr) => !dstr[attr] || dstr[attr].trim() === ''
		);
		if (missingAttributes) {
			this.logger.warn(
				'Missing required attributes from Criteo to create PinnedContent:',
				dstr
			);
			return null;
		}

		return {
			altText: dstr.header_text,
			csid: dst.LineItemID,
			cta: [{ link: dstr.redirect_url, linkTitle: dstr.button_text }],
			headline: dstr.header_text,
			onClickBeacon: dst.OnClickBeacon,
			onViewBeacon: dst.OnViewBeacon,
			pinnedPosition: CRITEO_DIGITAL_SHELF_TALKER_PINNED_POSITION,
			Subhead: dstr.subcopy_text,
			textColor: dstr.text_color?.toLowerCase(),
			visualAssets: [
				[
					{
						altText: dstr.header_text,
						type: 'imageUpload',
						value: dstr.background_image
					}
				]
			]
		};
	}
}
